<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import type { VForm } from 'vuetify/components'

import { useCalculationStore } from '@/stores/calculation'
import { useHardwareProfileStore } from '@/stores/hardwareProfile'
import type { CalculationReschedulePayload, HardwareProfile } from '@/stores/type/calculation.type'
import {
  DEFAULT_VM_MAX_RUNTIME_SECONDS,
  VM_MAX_RUNTIME_OPTIONS
} from '@/stores/type/calculation.type'

const props = defineProps<{
  modelValue: boolean
  calculationName?: string
  projectId?: number | null
  hardwareProfileId?: number | null
  containerImage?: string | null
  vmMaxRuntimeSeconds?: number | null
  loading?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  confirm: [payload: CalculationReschedulePayload]
}>()

const formRef = ref<VForm>()
const isValid = ref(false)
const selectedHardwareProfileId = ref<number | null>(null)
const selectedContainerImage = ref('')
const selectedVmMaxRuntimeSeconds = ref(DEFAULT_VM_MAX_RUNTIME_SECONDS)
const calculationStore = useCalculationStore()
const hardwareProfileStore = useHardwareProfileStore()
const { containerImages, containerImagesLoading, containerImagesError } =
  storeToRefs(calculationStore)
const { availableItems: hardwareProfiles, availableLoading: hardwareProfilesLoading } =
  storeToRefs(hardwareProfileStore)

const dialog = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value)
})

const containerImageItems = computed(() =>
  containerImages.value.map((image) => ({
    title: image.label,
    value: image.value
  }))
)

const sortHardwareProfilesByName = (profiles: HardwareProfile[]) => {
  return [...profiles].sort((left, right) => {
    const nameComparison = left.name.localeCompare(right.name, undefined, {
      sensitivity: 'base',
      numeric: true
    })
    if (nameComparison !== 0) return nameComparison
    return left.id - right.id
  })
}

const hardwareProfileItems = computed(() =>
  sortHardwareProfilesByName(hardwareProfiles.value).map((profile) => ({
    title: hardwareProfileTitle(profile),
    value: profile.id
  }))
)

const vmMaxRuntimeItems = computed(() =>
  VM_MAX_RUNTIME_OPTIONS.map((option) => ({
    title: option.label,
    value: option.value
  }))
)

const containerImageRules = [(value: string) => !!value || 'Required.']
const hardwareProfileRules = [(value: number | null) => !!value || 'Required.']
const vmMaxRuntimeRules = [(value: number | null) => !!value || 'Required.']

const hardwareProfileTitle = (profile: HardwareProfile) => {
  const resourceLabel = `${profile.cpu_cores} cores, ${profile.memory_gb} GB RAM`
  const spotLabel = profile.spot_instance ? ', spot' : ''
  const diskLabel = `${profile.vm_os_disk_size_gb ?? 32} GB OS disk`
  return `${profile.name} (VM, ${profile.vm_size || 'VM size'}, ${resourceLabel}, ${diskLabel}${spotLabel})`
}

const selectedImageExists = computed(() =>
  containerImageItems.value.some((image) => image.value === selectedContainerImage.value)
)

const selectedHardwareProfileExists = computed(() =>
  hardwareProfileItems.value.some((profile) => profile.value === selectedHardwareProfileId.value)
)

const confirmDisabled = computed(() => {
  return (
    Boolean(props.loading) ||
    hardwareProfilesLoading.value ||
    containerImagesLoading.value ||
    Boolean(containerImagesError.value) ||
    !selectedHardwareProfileExists.value ||
    !selectedImageExists.value
  )
})

const resetSelection = () => {
  selectedHardwareProfileId.value = props.hardwareProfileId ?? null
  selectedContainerImage.value = props.containerImage ?? ''
  selectedVmMaxRuntimeSeconds.value =
    Number(props.vmMaxRuntimeSeconds) || DEFAULT_VM_MAX_RUNTIME_SECONDS
  formRef.value?.resetValidation()
}

const refreshOptions = async () => {
  if (props.projectId) {
    await hardwareProfileStore.fetchAvailable(props.projectId)
  }
  await calculationStore.fetchContainerImages()
}

const close = () => {
  if (props.loading) return
  dialog.value = false
}

const confirm = async () => {
  const validationResult = await formRef.value?.validate()
  if (
    !validationResult?.valid ||
    !selectedContainerImage.value ||
    !selectedHardwareProfileId.value
  ) {
    return
  }
  emit('confirm', {
    containerImage: selectedContainerImage.value,
    hardwareProfile: selectedHardwareProfileId.value,
    vmMaxRuntimeSeconds: selectedVmMaxRuntimeSeconds.value
  })
}

watch(
  () => props.modelValue,
  (isOpen) => {
    if (!isOpen) return
    resetSelection()
    void refreshOptions()
  },
  { immediate: true }
)

watch(
  () => [props.containerImage, props.hardwareProfileId, props.vmMaxRuntimeSeconds],
  () => {
    if (!dialog.value) return
    resetSelection()
  }
)
</script>

<template>
  <v-dialog v-model="dialog" max-width="540" @click:outside="close">
    <v-card class="form-dialog pa-3">
      <v-card-title>Reschedule calculation</v-card-title>

      <v-card-text class="pl-4">
        <p class="mb-4">
          Are you sure you want to re-schedule {{ calculationName || 'this calculation' }}?
        </p>

        <v-form ref="formRef" v-model="isValid" @submit.prevent="confirm">
          <v-select
            v-model="selectedHardwareProfileId"
            color="primary"
            item-title="title"
            item-value="value"
            label="Hardware profile"
            :items="hardwareProfileItems"
            :loading="hardwareProfilesLoading"
            :menu-props="{ zIndex: 2500 }"
            :rules="hardwareProfileRules"
            variant="filled"
            required
          />

          <v-select
            v-model="selectedContainerImage"
            color="primary"
            item-title="title"
            item-value="value"
            label="Calculation image"
            :items="containerImageItems"
            :error-messages="containerImagesError ? [containerImagesError] : []"
            :loading="containerImagesLoading"
            :menu-props="{ zIndex: 2500 }"
            no-data-text="No images loaded from Azure Container Registry"
            :rules="containerImageRules"
            prepend-inner-icon="mdi-docker"
            variant="filled"
            required
          />

          <v-select
            v-model="selectedVmMaxRuntimeSeconds"
            color="primary"
            item-title="title"
            item-value="value"
            label="Maximum VM runtime"
            :items="vmMaxRuntimeItems"
            :menu-props="{ zIndex: 2500 }"
            :rules="vmMaxRuntimeRules"
            prepend-inner-icon="mdi-timer-outline"
            variant="filled"
            required
          />
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn class="text-none" color="primary" :disabled="loading" @click="close"> Cancel </v-btn>
        <v-btn
          class="text-none"
          color="primary"
          :loading="loading"
          :disabled="confirmDisabled"
          @click="confirm"
        >
          Reschedule
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
